/**
 * General purpose utility to help perform deep merges of two objects.
 * Javascript by its default nature only supports shallow merges.
 *
 * Primary used for taking big configuration objects with defaults and user overrides to combine
 * While lodash offers a capable `merge`, the follow public implementation below is also sufficient.
 */

/**
 * A helper function to check if a value is a mergeable object.
 * @param {*} item The value to check.
 * @returns {boolean} True if the item is a plain object and not an array.
 */
function isMergeableObject<T>(item: T) {
  return item && typeof item === "object" && !Array.isArray(item);
}

/**
 * A simple recursive deep merge function.
 * @param {object} target The object to merge into.
 * @param {...object} sources The objects to merge into the target.
 * @returns {object} A new deeply merged object.
 */
export function deepMerge<A extends object>(
  target: A = {} as A,
  ...sources: Array<object>
) {
  // If no sources, return the target
  if (sources.length === 0) {
    return target;
  }

  const source = sources.shift();

  // Handle the case where both target and source are mergeable objects
  if (source && isMergeableObject(target) && isMergeableObject(source)) {
    // Iterate over the keys in the source object
    Object.keys(source).forEach((key) => {
      const sourceValue = source[key as keyof typeof source];
      const targetValue = target[key as keyof typeof target];

      // If the key exists in both and both are mergeable, recurse
      if (isMergeableObject(sourceValue) && isMergeableObject(targetValue)) {
        target[key as keyof typeof target] = deepMerge(
          targetValue as object,
          sourceValue as object
        ) as typeof targetValue;
      } else if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        // Concatenate arrays instead of overwriting
        // The typings are too hard to figure out since this is a mixin from two other sources with unknown contents
        // @ts-ignore
        target[key as never] = [...targetValue, ...sourceValue];
      } else {
        // Otherwise, simply assign the source value, overwriting the target
        target[key as keyof typeof target] = sourceValue as typeof targetValue;
      }
    });
  }

  // Recurse for the remaining sources
  return deepMerge<A>(target, ...sources);
}
